import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCopy, FiCheck, FiSearch, FiArrowUpRight } from "react-icons/fi";
import { Helmet } from "react-helmet";
import axios from "axios";
import { REACT_APP_API_PORT } from "../Api";

/*
  Gemini Marketing Creative Codes Library
  - 30 Gemini marketing creative prompt-style codes
  - Search + one-click copy
  - Reference-inspired editorial design
  - Two extra power-user sections
  - Important: not every slash shortcut below is a native Gemini command; treat these as prompt-style marketing cues.
*/

const FREE_COMMAND_LIMIT = 10;
const UNLOCK_CACHE_KEY = "gemini_marketing_code_user";

const LEARN_URL = "https://learn.nextfutureai.in/web/checkout/6926a3a90b33bbee0d20d989?couponcode=FIRST50&purchaseNow=true";

const COMMANDS = [
  { id: 1, number: 1, command: "/BillboardAd", title: "BILLBOARDAD", description: "Giant highway billboard campaign" },
  { id: 2, number: 2, command: "/TimesSquareAd", title: "TIMESSQUAREAD", description: "Times Square digital billboard" },
  { id: 3, number: 3, command: "/AirportAd", title: "AIRPORTAD", description: "Premium airport advertising" },
  { id: 4, number: 4, command: "/LuxuryStoreDisplay", title: "LUXURYSTOREDISPLAY", description: "High-end retail window display" },
  { id: 5, number: 5, command: "/UnboxingAd", title: "UNBOXINGAD", description: "Viral creator unboxing" },
  { id: 6, number: 6, command: "/CinematicProductShot", title: "CINEMATICPRODUCTSHOT", description: "Hollywood-style product commercial" },
  { id: 7, number: 7, command: "/AppleLaunchStyle", title: "APPLELAUNCHSTYLE", description: "Minimal Apple-inspired launch" },
  { id: 8, number: 8, command: "/TechLaunchEvent", title: "TECHLAUNCHEVENT", description: "Futuristic keynote presentation" },
  { id: 9, number: 9, command: "/LuxuryBrandCampaign", title: "LUXURYBRANDCAMPAIGN", description: "Premium luxury brand aesthetic" },
  { id: 10, number: 10, command: "/MagazineCoverAd", title: "MAGAZINECOVERAD", description: "Fashion magazine cover" },
  { id: 11, number: 11, command: "/MagazineSpread", title: "MAGAZINESPREAD", description: "Double-page editorial advertisement" },
  { id: 12, number: 12, command: "/SubwayAd", title: "SUBWAYAD", description: "Metro station advertising" },
  { id: 13, number: 13, command: "/BusWrapAd", title: "BUSWRAPAD", description: "Full bus wrap campaign" },
  { id: 14, number: 14, command: "/TrainStationAd", title: "TRAINSTATIONAD", description: "Railway station billboard" },
  { id: 15, number: 15, command: "/ShoppingMallAd", title: "SHOPPINGMALLAD", description: "Interactive mall advertisement" },
  { id: 16, number: 16, command: "/TVCommercialFrame", title: "TVCOMMERCIALFRAME", description: "High-budget TV commercial scene" },
  { id: 17, number: 17, command: "/InstagramCarousel", title: "INSTAGRAMCAROUSEL", description: "Modern Instagram carousel creative" },
  { id: 18, number: 18, command: "/FacebookFeedAd", title: "FACEBOOKFEEDAD", description: "Facebook sponsored creative" },
  { id: 19, number: 19, command: "/PinterestAd", title: "PINTERESTAD", description: "Pinterest-inspired product showcase" },
  { id: 20, number: 20, command: "/TikTokProductAd", title: "TIKTOKPRODUCTAD", description: "Viral TikTok UGC style" },
  { id: 21, number: 21, command: "/GamingSetupAd", title: "GAMINGSETUPAD", description: "Product featured in an RGB gaming room" },
  { id: 22, number: 22, command: "/CafePlacement", title: "CAFEPLACEMENT", description: "Natural café product placement" },
  { id: 23, number: 23, command: "/LifestyleTravelAd", title: "LIFESTYLETRAVELAD", description: "Luxury travel lifestyle campaign" },
  { id: 24, number: 24, command: "/AwardWinningAd", title: "AWARDWINNINGAD", description: "Cannes Lions-worthy concept ad" },
  { id: 25, number: 25, command: "/NeonNightAd", title: "NEONNIGHTAD", description: "Cyberpunk neon campaign" },
  { id: 26, number: 26, command: "/EcoBrandAd", title: "ECOBRANDAD", description: "Sustainable eco-friendly branding" },
  { id: 27, number: 27, command: "/SciFiProductAd", title: "SCIFIPRODUCTAD", description: "Futuristic sci-fi advertisement" },
  { id: 28, number: 28, command: "/MinimalPosterAd", title: "MINIMALPOSTERAD", description: "Ultra-clean minimalist poster" },
  { id: 29, number: 29, command: "/StudioProductShoot", title: "STUDIOPRODUCTSHOOT", description: "Premium studio photography" },
  { id: 30, number: 30, command: "/LuxuryGiftBoxAd", title: "LUXURYGIFTBOXAD", description: "Elegant premium gift presentation" },
];

const CATEGORY_GROUPS = [
  { name: "Outdoor & Billboard Ads", range: [0, 3] },
  { name: "Retail & Product Ads", range: [3, 9] },
  { name: "Editorial & Fashion", range: [9, 11] },
  { name: "Transit & OOH", range: [11, 16] },
  { name: "Social Media Ads", range: [16, 20] },
  { name: "Lifestyle & Brand", range: [20, 25] },
  { name: "Creative & Premium", range: [25, 30] },
];

const comboSections = [
  [
    "Best Gemini Marketing Code Combinations",
    [
      ["Billboard Campaign", "/BillboardAd /TimesSquareAd /LuxuryBrandCampaign", "Create a high-impact outdoor campaign with a premium brand aesthetic across major billboard placements."],
      ["Cinematic Product Launch", "/CinematicProductShot /AppleLaunchStyle /TechLaunchEvent", "Build a polished product launch concept with cinematic product visuals and a futuristic keynote feel."],
      ["Luxury Retail Campaign", "/LuxuryStoreDisplay /ShoppingMallAd /AirportAd", "Create a premium retail campaign designed for high-visibility physical advertising spaces."],
      ["Social Media Ad Set", "/InstagramCarousel /FacebookFeedAd /TikTokProductAd", "Adapt one product campaign into modern social creatives for Instagram, Facebook and TikTok."],
      ["Viral Unboxing Campaign", "/UnboxingAd /TikTokProductAd /CafePlacement", "Create a creator-style product campaign with a natural, shareable UGC feel."],
      ["Fashion Editorial Campaign", "/MagazineCoverAd /MagazineSpread /LuxuryBrandCampaign", "Create a premium fashion editorial campaign with a luxury magazine aesthetic."],
      ["Transit Advertising Campaign", "/SubwayAd /BusWrapAd /TrainStationAd", "Turn one campaign idea into a large-scale transit advertising concept."],
      ["Gaming Product Campaign", "/GamingSetupAd /SciFiProductAd /NeonNightAd", "Create a futuristic gaming-focused product campaign with cyberpunk visual direction."],
      ["Eco Lifestyle Campaign", "/EcoBrandAd /LifestyleTravelAd /CafePlacement", "Create a sustainable lifestyle campaign with natural, premium product placement."],
      ["Premium Gift Campaign", "/LuxuryGiftBoxAd /StudioProductShoot /MinimalPosterAd", "Create an elegant premium gift campaign with clean studio photography and minimalist presentation."]
    ]
  ],
  [
    "How to Use Gemini Marketing Creative Codes Correctly",
    [
      ["STEP 1 — Choose your format", "Start with the ad format that matches your goal, such as /BillboardAd, /InstagramCarousel or /TikTokProductAd."],
      ["STEP 2 — Add your product", "Describe the product, brand, target audience, offer and the main message you want the creative to communicate."],
      ["STEP 3 — Choose the platform", "Use a code that matches where the ad will appear, such as a billboard, magazine, mall, social feed or TikTok."],
      ["STEP 4 — Add visual direction", "Describe the desired mood, composition, lighting, colors, environment, typography and overall brand style."],
      ["STEP 5 — Combine compatible codes", "Combine a few related codes to build a stronger campaign concept without mixing unrelated formats."],
      ["PRO TIP — Keep the campaign consistent", "Use the same product, brand identity, color language and core message across every creative format."],
      ["PRO TIP — Adapt for each platform", "Keep the main campaign idea consistent while changing the layout, framing and messaging for each platform."],
      ["PRO TIP — Expand the shortcut", "Treat the slash code as a creative starting point and add natural-language details for a more specific result."]
    ]
  ]
];
export default function GeminiGuide() {
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState("All");
  const [copied, setCopied] = useState("");

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", mobile: "" });
  const [leadError, setLeadError] = useState("");
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isSavingMobile, setIsSavingMobile] = useState(false);
  const unlockTriggerRef = useRef(null);

  useEffect(() => {
    try {
      const saved =
        localStorage.getItem(UNLOCK_CACHE_KEY) ||
        sessionStorage.getItem(UNLOCK_CACHE_KEY);

      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.name && parsed?.email && parsed?.mobile) {
          setLeadForm(parsed);
          setIsUnlocked(true);
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (isUnlocked || COMMANDS.length <= FREE_COMMAND_LIMIT || search.trim() || activeGroup !== "All") return;

    try {
      if (sessionStorage.getItem("gemini_marketing_code_popup_shown") === "1") return;
    } catch {}

    const target = unlockTriggerRef.current;
    if (!target || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        setShowUnlockModal(true);

        try {
          sessionStorage.setItem("gemini_marketing_code_popup_shown", "1");
        } catch {}

        observer.disconnect();
      },
      { root: null, rootMargin: "0px 0px 120px 0px", threshold: 0.01 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isUnlocked, search, activeGroup]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return COMMANDS.filter((item, index) => {
      const group = CATEGORY_GROUPS.find(g => index >= g.range[0] && index < g.range[1])?.name;
      const groupMatch = activeGroup === "All" || group === activeGroup;
      const searchMatch = !q || `${item.command} ${item.title} ${item.description} ${group}`.toLowerCase().includes(q);
      return groupMatch && searchMatch;
    });
  }, [search, activeGroup]);

  const copy = async (item) => {
    if (!isUnlocked && item.number > FREE_COMMAND_LIMIT) {
      setShowUnlockModal(true);
      return;
    }

    const text = `${item.command}\n${item.description}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(item.command);
      setTimeout(() => setCopied(""), 1400);
    } catch {}
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    const name = leadForm.name.trim();
    const email = leadForm.email.trim().toLowerCase();
    const mobile = leadForm.mobile.trim();

    if (!name) {
      setLeadError("Please enter your name");
      return;
    }

    if (!email) {
      setLeadError("Please enter your email");
      return;
    }

    if (!mobile) {
      setLeadError("Please enter your mobile number");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadError("Please enter a valid email.");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(mobile)) {
    setLeadError("Please enter a valid 10-digit mobile number.");
    return;
  }

    try {
      setIsSavingMobile(true);
      setLeadError("");

      const res = await axios.post(
      `${REACT_APP_API_PORT}user/homepage/guide_users_gemini`,
      {
        name,
        email,
        mobile,
      }
    );

      if (!res.data?.status) {
        setLeadError(
          res.data?.message || "Something went wrong. Please try again."
        );
        return;
      }

      const userDetails = { name, email, mobile };

      try {
        localStorage.setItem(
          UNLOCK_CACHE_KEY,
          JSON.stringify(userDetails)
        );
        sessionStorage.setItem(
          UNLOCK_CACHE_KEY,
          JSON.stringify(userDetails)
        );
      } catch {}

      setLeadForm(userDetails);
      setIsUnlocked(true);
      setShowUnlockModal(false);
      setLeadError("");
    } catch (error) {
      console.error("Unlock API Error:", error);
      setLeadError(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setIsSavingMobile(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] text-[#FFFFFF]">
      <Helmet>
        <title>30 Gemini Marketing Creative Codes 2026 | Gemini Guide</title>
        <meta name="description" content="Explore 30 Gemini marketing creative codes for ads, campaigns, social media, retail, editorial and product concepts." />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,600;0,700;1,600;1,700&display=swap');
        .cmd-serif { font-family:'Playfair Display', Georgia, serif; }
        .cmd-sans { font-family:'DM Sans', Arial, sans-serif; }
        html, body, #root { min-height:100%; margin:0; }
        body { background:#000000; color:#FFFFFF; }
        .claude-bg { min-height:100vh; overflow-x:hidden; overflow-y:visible; background:linear-gradient(180deg,#000000 0%,#000000 100%); }
        .shortcut-card { box-shadow:0 10px 28px rgba(0,0,0,.35); }
        .shortcut-card:hover { box-shadow:0 16px 34px rgba(0,0,0,.55); transform:translateY(-2px); }
        .learn-strip:hover { border-color:#111111; background:rgba(255,255,255,.08); }
        .scrollbar-hide { scrollbar-width:none; -ms-overflow-style:none; }
        .scrollbar-hide::-webkit-scrollbar { display:none; }
      `}</style>

      <main className="cmd-sans claude-bg min-h-screen">
        <section className="mx-auto max-w-[1260px] px-5 pb-10 pt-9 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-[980px] text-center">
            <div className="inline-flex items-center rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-5 py-2 text-[10px] font-bold uppercase tracking-[.18em] text-white shadow-[0_8px_22px_rgba(255,255,255,.10)]">
              ✦ Gemini Marketing Creative Code Library
            </div>
            <h1 className="cmd-serif mt-6 text-[40px] font-bold leading-[1.05] tracking-[-.035em] text-[#FFFFFF] sm:text-[58px]">
              30 Marketing <span className="block italic text-[#FFFFFF]">Creative Codes 2026</span>
            </h1>
            <div className="mx-auto mt-4 h-[3px] w-14 rounded-full bg-[#111111]" />
            <p className="mx-auto mt-6 max-w-[790px] text-[14px] leading-6 text-[#AAAAAA] sm:text-[16px]">
              Slash-command style prompts for generating ad concepts across formats and platforms.
            </p>
            <div className="mt-5 text-[11px] leading-5 text-[#999999]">
              These are <strong>prompt-style marketing cues</strong>; use them as shorthand and expand them into natural-language instructions when needed.
            </div>
            <div className="mt-7 flex flex-wrap justify-center gap-3 text-[10px] font-bold uppercase tracking-[.12em] text-[#111]">
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">⚡ 30 Secret Codes</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">📢 Ads + Campaigns</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">🎨 Creative + Brand</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">📋 One-Click Copy</span>
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-[1040px] rounded-[26px] border border-[#2A2A2A] bg-[#0A0A0A]/80 p-5 shadow-[0_18px_55px_rgba(0,0,0,.45)] sm:p-7">
            <h2 className="cmd-serif text-[24px] font-bold text-[#FFFFFF]">🎯 How to use these secret codes</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                ["STEP 1", "Pick a code", "Choose the ad format, platform or campaign style that matches your goal."],
                ["STEP 2", "Add your product", "Describe your product, brand, audience, offer and the visual direction."],
                ["STEP 3", "Copy & use", "Copy the code and combine it with your natural-language marketing prompt."],
              ].map(([a,b,c]) => (
                <div key={a} className="rounded-[18px] border border-[#2A2A2A] bg-white/5 p-5">
                  <div className="text-[10px] font-bold tracking-[.15em] text-[#FFFFFF]">{a}</div>
                  <div className="mt-2 text-[15px] font-bold text-[#EEEEEE]">{b}</div>
                  <p className="mt-1 text-[12px] leading-5 text-[#AAAAAA]">{c}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1260px] px-5 pb-16 sm:px-8 lg:px-10">
          <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em] text-white">ALL 30 MARKETING CREATIVE CODES</div>
              <h2 className="cmd-serif mt-4 text-[34px] font-bold tracking-[-.03em] text-[#FFFFFF] sm:text-[46px]">The Complete Secret Code Library</h2>
              <p className="mt-2 text-sm text-[#AAAAAA]">Search, filter and copy any Gemini marketing code in seconds.</p>
            </div>
            <div className="w-full lg:w-[370px]">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999]" />
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search marketing codes, ad formats or uses..." className="h-12 w-full rounded-2xl border border-[#2A2A2A] bg-white/5 pl-11 pr-4 text-sm text-[#FFFFFF] outline-none transition focus:border-[#111111] focus:ring-4 focus:ring-[#111111]/10" />
              </div>
            </div>
          </div>

          <div className="mb-8 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button onClick={()=>setActiveGroup("All")} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition ${activeGroup === "All" ? "bg-[#FFFFFF] text-black" : "border border-[#2A2A2A] bg-white/5 text-[#fff] hover:border-[#555555]"}`}>All · 30</button>
            {CATEGORY_GROUPS.map(group => (
              <button key={group.name} onClick={()=>setActiveGroup(group.name)} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition ${activeGroup === group.name ? "bg-[#FFFFFF] text-black" : "border border-[#2A2A2A] bg-white/5 text-[#fff] hover:border-[#555555]"}`}>{group.name}</button>
            ))}
          </div>

          <div className="mb-5 text-sm font-semibold text-[#AAAAAA]">Showing <strong>{filtered.length}</strong> creative code{filtered.length === 1 ? "" : "s"}</div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
  {filtered.map((item, idx) => (
    <React.Fragment key={item.id}>

      <article className="shortcut-card group relative rounded-[20px] border border-[#E8E8E8] bg-[#FFFFFF] p-5 transition duration-200 hover:-translate-y-0.5">

        {!isUnlocked && item.number > FREE_COMMAND_LIMIT && (
          <div
            className="absolute inset-0 z-20 flex cursor-pointer flex-col items-center justify-center rounded-[20px] bg-[#111111]/90 px-5 text-center backdrop-blur-[3px]"
            onClick={() => setShowUnlockModal(true)}
          >
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-[#444444] bg-[#222222] text-lg">
              🔒
            </div>
            <p className="text-sm font-bold text-white">
              This Shortcut is Locked
            </p>
            <p className="mt-1 text-[11px] leading-4 text-[#999999]">
              Submit your details to unlock additional marketing resources
            </p>
          </div>
        )}

        {/* Pink left accent */}
        <div className="absolute left-0 top-5 h-10 w-[3px] rounded-r-full bg-[#FF5F87]" />

        <div className="flex items-start justify-between gap-3 pl-2">

          <div className="min-w-0 flex-1">

            <div className="flex items-center justify-between gap-2">

              {/* Command */}
              <code className="rounded-lg bg-[#FFF1F4] px-2.5 py-1 text-[15px] font-bold tracking-[-.02em] text-[#111111]">
                {item.command}
              </code>

              {/* Number */}
              <span className="shrink-0 rounded-full bg-[#111111] px-2 py-1 text-[8px] font-bold uppercase tracking-[.05em] text-white">
                #{String(item.id).padStart(3, "0")}
              </span>

            </div>

            {/* Category */}
            <div className="mt-4 text-[10px] font-bold uppercase tracking-[.12em] text-[#111111]">
              {item.title}
            </div>

            {/* Description */}
            <p className="mt-2 text-[13px] leading-5 text-[#6B4A55]">
              {item.description}
            </p>

          </div>

          {/* Copy Button */}
          <button
            onClick={() => copy(item)}
            aria-label={`Copy ${item.command}`}
            className={`shrink-0 rounded-xl p-2.5 transition ${
              copied === item.command
                ? "bg-[#FF5F87] text-white"
                : "bg-[#FFF1F4] text-[#9A6073] hover:bg-[#FF5F87] hover:text-white"
            }`}
          >
            {copied === item.command ? <FiCheck /> : <FiCopy />}
          </button>

        </div>

        {/* Bottom CTA */}
        <div className="mt-4 border-t border-[#E8DDE1] pt-3 text-[10px] font-semibold uppercase tracking-[.10em] text-[#9A7A84]">
          Type{" "}
          <span className="text-[#FF5F87]">
            {item.command}
          </span>{" "}
          + your request

          <a
            href={LEARN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="float-right font-bold text-[#FF5F87] hover:underline"
          >
            LEARN HOW →
          </a>
        </div>

      </article>

      {item.number === FREE_COMMAND_LIMIT &&
        !isUnlocked &&
        !search.trim() &&
        activeGroup === "All" && (
          <div
            ref={unlockTriggerRef}
            className="col-span-full h-2"
            aria-hidden="true"
          />
        )}

      {/* Learn More Strip */}
      {(idx + 1) % 12 === 0 && idx !== filtered.length - 1 && (
        <a
          href={LEARN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="learn-strip md:col-span-2 xl:col-span-3 rounded-[18px] border border-[#2A2A2A] bg-[#181818] px-5 py-3.5 transition hover:bg-[#202020]"
        >
          <div className="flex items-center justify-between gap-4">

            <div>
              <div className="text-[9px] font-bold uppercase tracking-[.16em] text-[#FF5F87]">
                Keep creating
              </div>

              <div className="mt-1 text-[12px] font-semibold text-[#CCCCCC]">
                Explore practical AI marketing workflows and prompting techniques.
              </div>
            </div>

            <span className="shrink-0 text-[10px] font-bold uppercase tracking-[.12em] text-[#FF5F87]">
              Learn more →
            </span>

          </div>
        </a>
      )}

    </React.Fragment>
  ))}
</div>

          {filtered.length === 0 && (
            <div className="rounded-[24px] border border-[#2A2A2A] bg-white/5 p-14 text-center">
              <div className="text-4xl">🔎</div>
              <h3 className="cmd-serif mt-4 text-2xl font-bold text-[#FFFFFF]">No codes found</h3>
              <p className="mt-2 text-sm text-[#AAAAAA]">Try another keyword, ad format or creative style.</p>
            </div>
          )}
        </section>
       <section className="mx-auto max-w-[1140px] px-5 pb-14 sm:px-8">
         <div className="mb-4 inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em]">
           🎬 MARKETING PROMPT EXAMPLES
         </div>
         <h2 className="cmd-serif text-center text-[32px] font-bold text-[#FFFFFF] sm:text-[40px]">Turn Codes Into Better Ad Prompts</h2>
         <div className="mt-8 grid gap-4 md:grid-cols-2">
           {[
             ["Luxury Billboard Campaign", "/BillboardAd /LuxuryBrandCampaign", "Create a premium highway billboard for a luxury brand, bold product focus, minimal copy, high-end lighting, clean composition and an instantly recognizable visual identity."],
             ["Cinematic Product Commercial", "/CinematicProductShot /StudioProductShoot", "Create a cinematic product advertisement with premium studio lighting, dramatic composition, realistic materials, hero product framing and a polished commercial aesthetic."],
             ["Viral Social Product Ad", "/TikTokProductAd /UnboxingAd", "Create a short-form creator-style product ad with a strong visual hook, authentic unboxing moment, natural environment and scroll-stopping composition."],
             ["Luxury Fashion Editorial", "/MagazineCoverAd /MagazineSpread /LuxuryBrandCampaign", "Create a premium fashion editorial campaign with magazine-cover composition, elegant styling, sophisticated lighting and a luxury brand aesthetic."]
           ].map(([name, codes, prompt]) => (
             <div key={name} className="rounded-[20px] border border-[#2A2A2A] bg-[#0D0D0D] p-5">
               <div className="text-[9px] font-bold uppercase tracking-[.16em] text-[#999999]">{name}</div>
               <code className="mt-3 block rounded-xl border border-[#292929] bg-[#181818] px-3 py-2 text-[10px] font-semibold leading-5 text-[#FFFFFF]">{codes}</code>
               <p className="mt-3 text-[12px] leading-5 text-[#AAAAAA]">{prompt}</p>
             </div>
           ))}
         </div>
       </section>
       <section className="mx-auto max-w-[1140px] px-5 pb-20 sm:px-8">

  {/* Section Label */}
  <div className="mb-4 inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em]">
    💡 HOW TO USE GEMINI MARKETING CODES
  </div>

  {/* Heading */}
  <h2 className="cmd-serif text-center text-[32px] font-bold text-[#FFFFFF] sm:text-[40px]">
    Use Gemini Marketing Codes Correctly
  </h2>

  {/* Underline */}
  

  {/* Cards */}
  <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">

    {(Array.isArray(comboSections[1]?.[1])
      ? comboSections[1][1]
      : []
    ).map(([title, body], i) => (

      <div
        key={title}
        className="group relative rounded-[20px] border border-[#2A2A2A] bg-[#0D0D0D] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-[#444444] hover:bg-[#111111]"
      >

        <div className="flex items-start gap-4">

          {/* Number */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#2A2A2A] bg-[#181818] text-[12px] font-bold text-[#FFFFFF] transition group-hover:border-[#FF5F87] group-hover:text-[#FF5F87]">
            {i + 1}
          </div>

          <div className="min-w-0 flex-1">

            {/* Title */}
            <div className="text-[13px] font-bold leading-5 text-[#FFFFFF]">
              {title}
            </div>

         
            {/* Description */}
            <p className="mt-2 text-[12px] leading-5 text-[#999999]">
              {body}
            </p>

          </div>

        </div>

      </div>

    ))}

  </div>
</section>

        {showUnlockModal && !isUnlocked && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm"
            onClick={() => setShowUnlockModal(false)}
          >
            <div
              className="relative max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-[24px] border border-[#333333] bg-[#181818] p-5 shadow-[0_20px_70px_rgba(0,0,0,.55)] sm:p-7"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowUnlockModal(false)}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-[#333333] bg-[#222222] text-[#AAAAAA] transition hover:text-white"
                aria-label="Close"
              >
                ×
              </button>

              <div className="pr-8">
                <div className="inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-3 py-1 text-[9px] font-bold uppercase tracking-[.14em] text-white">
                  🔒 Unlock Full Library
                </div>

                <h3 className="cmd-serif mt-4 text-[28px] font-bold leading-tight text-white">
                  Unlock Marketing Resources
                </h3>

                <p className="mt-3 text-[13px] leading-5 text-[#AAAAAA]">
                  The marketing creative codes are available above. Enter your details to
                  unlock additional AI marketing resources.
                </p>
              </div>

              <form onSubmit={handleLeadSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-[#CCCCCC]">
                    Name
                  </label>
                  <input
                    type="text"
                    value={leadForm.name}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter your name"
                    className="h-12 w-full rounded-xl border border-[#333333] bg-[#0D0D0D] px-4 text-sm text-white outline-none transition placeholder:text-[#666666] focus:border-[#FF5F87]"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-[#CCCCCC]">
                    Email
                  </label>
                  <input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="Enter your email"
                    className="h-12 w-full rounded-xl border border-[#333333] bg-[#0D0D0D] px-4 text-sm text-white outline-none transition placeholder:text-[#666666] focus:border-[#FF5F87]"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-[#CCCCCC]">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={leadForm.mobile}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        mobile: e.target.value.replace(/\\D/g, "").slice(0, 10),
                      }))
                    }
                    placeholder="Enter 10-digit mobile number"
                    className="h-12 w-full rounded-xl border border-[#333333] bg-[#0D0D0D] px-4 text-sm text-white outline-none transition placeholder:text-[#666666] focus:border-[#FF5F87]"
                  />
                </div>

                {leadError && (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-[11px] leading-4 text-red-300">
                    {leadError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSavingMobile}
                  className="flex h-12 w-full items-center justify-center rounded-xl bg-white px-5 text-xs font-bold uppercase tracking-[.08em] text-black transition hover:bg-[#FF5F87] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingMobile ? "Unlocking..." : "Unlock Marketing Resources →"}
                </button>

                <p className="text-center text-[10px] leading-4 text-[#777777]">
                  Your email will be used for future AI updates &amp; resources.
                </p>
              </form>
            </div>
          </div>
        )}

        <footer className="border-t border-[#2A2A2A] bg-[#080808] py-10 text-center">
          <div className="cmd-serif text-[22px] font-bold text-[#FFFFFF]">30 Gemini Marketing Creative Codes 2026</div>
          <p className="mx-auto mt-2 max-w-[700px] px-5 text-xs leading-5 text-[#AAAAAA]">Use these as reusable marketing prompt cues. Results can vary by model, wording, product, platform and generation settings.</p>
          <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.12em] text-[#FFFFFF] transition hover:text-[#FFFFFF] hover:underline">Learn more about practical AI workflows <FiArrowUpRight/></a>
        </footer>
      </main>
    </div>
  );
}
